/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, useMemo, useState } from 'react';
import { message, Modal, Spin, Tabs } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import { queryCache, useMutation } from 'react-query';
import { cloneDeep } from 'lodash';

import { ThemedStyleFn } from '~src/hooks/useStyles';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import {
  AlteredDbFolderPermission,
  ProjectWideTab,
  ProjectWideTabProps,
} from './Tabs/ProjectWideTab';
import { useSecurityGroups } from '~src/hooks/api-hooks/useSecurityGroups';
import { useFolderPermissions } from '~src/hooks/api-hooks/useFolderPermissions';
import {
  createCommit,
  createFolderPermission,
  CreateFolderPermissionParams,
  deleteCommit,
  deleteFolderPermission,
  idOrNull,
  updateCommit,
  updateFolderPermission,
} from '~src/services/api';
import { DbCommit, DbFolder, DbFolderPermission } from '~src/types/db';
import { useProjectId } from '~src/hooks/useProjectId';
import { appLog } from '~src/utils/log';
import { SingleGroupTab } from './Tabs/SingleGroupTab';
import { useModalVisibleChanged } from '~src/hooks/modal/useModalVisibleChanged';
import { useIsSuperuser } from '~src/hooks/useIsSuperuser';
import { DB_STATE } from '~src/types/enums';

interface RightsModalProps extends Partial<ModalProps> {
  folder?: DbFolder;
}

export const RightsModal: React.FC<RightsModalProps> = ({
  onCancel,
  folder,
  afterClose,
  ...modalProps
}) => {
  const intl = useIntl();
  const projectId = useProjectId();
  const { modalAppeared } = useModalVisibleChanged(!!modalProps.visible);
  const isSuperuser = useIsSuperuser();

  const foldersQuery = useFolders(
    {
      as_admin: isSuperuser ? true : undefined,
      show_deleted: false,
    },
    {
      // will refetch on modal show, if stale
      enabled: modalAppeared,
      staleTime: 60 * 1000, // 1min
    }
  );
  const secGroupsQuery = useSecurityGroups({
    // will refetch on modal show, if stale
    enabled: modalAppeared,
    staleTime: 60 * 1000, // 1min
    onError: (err) => {
      message.error(intl.formatMessage({ id: 'security-groups-load-failed' }));
      // @ts-ignore
      onCancel && onCancel();
      appLog('RightsModal -> secGroupsQuery -> err', err);
    },
  });
  const folderPermissionsQuery = useFolderPermissions(
    { folderIds: (foldersQuery.data || []).map((f) => String(f.id)) },
    {
      // will refetch on modal show, if stale AND foldersQuery.data is fresh
      // enabled: modalAppeared && foldersQuery.data && !foldersQuery.isStale,
      enabled: true,
      // staleTime: 120 * 1000, // 2min
      onError: (err) => {
        message.error(
          intl.formatMessage({ id: 'security-groups-load-failed' })
        );
        // @ts-ignore
        onCancel && onCancel();
        appLog('RightsModal -> folderPermissionsQuery -> err', err);
      },
    }
  );

  const [isLoadingAfterUpdate, setIsLoadingAfterUpdate] = useState(false);

  const [doUpdatePermissions, updatePermissionsMutation] = useMutation(
    updatePermissions,
    {
      onError: (error, items) => {
        message.error(intl.formatMessage({ id: 'rights-update-failed' }));
        appLog('RightsModal -> updatePermissions -> items', items);
        appLog('RightsModal -> updatePermissions -> err', error);
        items.commit &&
          deleteCommit({
            projectId: String(projectId),
            commit: items.commit,
          }).catch(() => undefined);
      },
      onSuccess: async (data, { commit }) => {
        setIsLoadingAfterUpdate(true);
        queryCache.invalidateQueries('folderPermissions');
        queryCache.invalidateQueries('folders');
        commit &&
          (await updateCommit({
            projectId: String(projectId),
            commitData: { ...commit, state: DB_STATE.PROCESSED },
          }));

        // setTimeout(() => {
        // reset changes
        setAddedPermissions([]);
        setRemovedPermissions([]);
        // }, 200);
      },
    }
  );

  /** holds permissions to be created */
  const [addedPermissions, setAddedPermissions] = useState<
    Array<
      AlteredDbFolderPermission | CreateFolderPermissionParams['permissionData']
    >
  >([]);
  /** holds permissions to be removed */
  const [removedPermissions, setRemovedPermissions] = useState<
    DbFolderPermission[]
  >([]);

  const fetching = useMemo(() => {
    if (
      foldersQuery.isFetching ||
      secGroupsQuery.isFetching ||
      folderPermissionsQuery.isFetching
    ) {
      return true;
    } else {
      return false;
    }
  }, [
    foldersQuery.isFetching,
    secGroupsQuery.isFetching,
    folderPermissionsQuery.isFetching,
  ]);

  const loading = useMemo(() => {
    if (
      updatePermissionsMutation.isLoading ||
      foldersQuery.isLoading ||
      secGroupsQuery.isLoading ||
      folderPermissionsQuery.isLoading ||
      fetching
    ) {
      return true;
    } else {
      return false;
    }
  }, [
    updatePermissionsMutation.isLoading,
    foldersQuery.isLoading,
    secGroupsQuery.isLoading,
    folderPermissionsQuery.isLoading,
    fetching,
  ]);

  useEffect(() => {
    if (!loading && !fetching) {
      setIsLoadingAfterUpdate(false);
    }
  }, [loading, fetching]);

  /** Add permission to "addedPermissions", for save */
  const addPermission = (
    permission: CreateFolderPermissionParams['permissionData']
  ) => {
    // if permission in addedPermissions, update it
    const addedIndex = addedPermissions.findIndex(
      (updated) =>
        updated.folder_id === permission.folder_id &&
        updated.security_group_id === permission.security_group_id
    );
    if (addedIndex > -1) {
      return setAddedPermissions((prevAdded) => {
        const nextAdded = [...prevAdded];
        nextAdded[addedIndex] = { ...nextAdded[addedIndex], ...permission };
        return nextAdded;
      });
    }

    // if permission was removed before, add it back aka remove from removed ;)
    const foundInRemoved = removedPermissions.find(
      (removed) =>
        removed.folder_id === permission.folder_id &&
        removed.security_group_id === permission.security_group_id
    );
    if (foundInRemoved) {
      // if levels don't match, means permission was added in other column, so push in addedPermissions
      if (foundInRemoved.level !== permission.level) {
        setAddedPermissions((prevAdded) => [
          ...prevAdded,
          { ...foundInRemoved, ...permission, altered: true },
        ]);
      }
      return setRemovedPermissions((prevRemoved) =>
        prevRemoved.filter((v) => v.id !== foundInRemoved.id)
      );
    }

    // if dbPermission, add it as `altered`
    const dbPermission = folderPermissionsQuery.data
      ?.find((v) => v.folderId === idOrNull(permission.folder_id))
      ?.permissions.find(
        (p) => p.security_group_id === permission.security_group_id
      );

    if (dbPermission) {
      const alteredPermission: AlteredDbFolderPermission = {
        ...dbPermission,
        ...permission,
        altered: true,
      };
      return setAddedPermissions((prevAdded) => [
        ...prevAdded,
        alteredPermission,
      ]);
    }

    // if none of the above, just push permission data
    setAddedPermissions((prevAdded) => [...prevAdded, permission]);
  };

  const doCreateCommit = async () => {
    if (!projectId) throw new Error('Missing project id.');

    const commitData = {
      description: `Restore files for project id = ${projectId}`,
      // TO DO: Check if important should be true/false
      is_important: true,
      state: DB_STATE.DRAFT,
    };

    return createCommit({
      projectId: String(projectId),
      commitData,
    }).catch((err) => {
      message.error(intl.formatMessage({ id: 'commit-create-failed' }));
      appLog('Restore files -> createCommit -> commit', commitData);
      appLog('Restore files -> createCommit -> err', err);
      throw err;
    });
  };

  /** Add permission to "removedPermissions", for save */
  const removePermission = (
    permission: CreateFolderPermissionParams['permissionData']
  ) => {
    const foundIndex = addedPermissions.findIndex(
      (added) =>
        added.folder_id === permission.folder_id &&
        added.security_group_id === permission.security_group_id &&
        added.level === permission.level
    );

    // if permission was added before, remove it ;)
    if (foundIndex > -1) {
      return setAddedPermissions((prevAdded) => {
        const nextAdded = [...prevAdded];
        nextAdded.splice(foundIndex, 1);
        return nextAdded;
      });
    }

    // if permission was in db, push in `removedPermissions`
    const dbPermission = folderPermissionsQuery.data
      ?.find((v) => v.folderId === idOrNull(permission.folder_id))
      ?.permissions.find(
        (p) =>
          p.security_group_id === permission.security_group_id &&
          p.level === permission.level
      );

    dbPermission &&
      setRemovedPermissions((prevRemoved) => [...prevRemoved, dbPermission]);
  };

  const handleSaveClick = async () => {
    setIsLoadingAfterUpdate(true);
    if (!addedPermissions.length && !removedPermissions.length) return;
    const commit = await doCreateCommit();
    doUpdatePermissions({
      projectId: String(projectId),
      addedPermissions,
      removedPermissions,
      commit,
    });
  };

  /** combine permissions from db with added/removed */
  let currentPermissions: ProjectWideTabProps['permissionList'] = [];

  if (secGroupsQuery.data && folderPermissionsQuery.data) {
    currentPermissions = cloneDeep(folderPermissionsQuery.data);

    // filter out removedPermissions
    removedPermissions.forEach((removed) => {
      const i = currentPermissions.findIndex(
        (v) => v.folderId === idOrNull(removed.folder_id)
      );
      if (i < 0) return;

      currentPermissions[i].permissions = currentPermissions[
        i
        // @ts-ignore
      ].permissions.filter((v) => !v.id || v.id !== removed.id);
    });

    // add addedPermissions
    addedPermissions.forEach((added) => {
      const i = currentPermissions.findIndex(
        (v) => v.folderId === idOrNull(added.folder_id)
      );
      if (i < 0) {
        currentPermissions.push({
          folderId: idOrNull(added.folder_id),
          permissions: [added],
        });
        return;
      }

      // replace addedPermissions:altered
      if ((added as AlteredDbFolderPermission).altered) {
        const existingIndex = currentPermissions[i].permissions.findIndex(
          (v) => v.security_group_id === added.security_group_id
        );
        if (existingIndex < 0) return;

        currentPermissions[i].permissions.splice(existingIndex, 1, added);
        return;
      }

      currentPermissions[i].permissions.push(added);
    });
  }

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'group-rights' })}
          icon={['far', 'file-user']}
        />
      }
      okText={intl.formatMessage({ id: 'save' })}
      okButtonProps={{
        disabled: !addedPermissions.length && !removedPermissions.length,
        loading:
          updatePermissionsMutation.isLoading ||
          (fetching && !loading) ||
          isLoadingAfterUpdate,
      }}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width="min(95vw, 1150px)"
      centered
      // to reset expanded table rows after close
      destroyOnClose
      confirmLoading={loading}
      maskClosable={false}
      onCancel={onCancel}
      onOk={handleSaveClick}
      afterClose={() => {
        // reset changes on dismiss
        setAddedPermissions([]);
        setRemovedPermissions([]);
        if (updatePermissionsMutation.isError) {
          queryCache.invalidateQueries('folderPermissions');
        }

        // use provided callback if existent
        afterClose && afterClose();
      }}
      footer={
        window.sessionStorage.getItem('appState') !== 'timeMachine' && undefined
      }
      {...modalProps}>
      <Tabs
        css={{ marginTop: -40 }}
        tabBarExtraContent={
          fetching && !loading ? <Spin size="small" /> : undefined
        }>
        <Tabs.TabPane
          style={{ marginTop: 8 }}
          key="project-wide"
          tab={intl.formatMessage({ id: 'project-wide' })}>
          <ProjectWideTab
            folder={folder}
            permissionList={currentPermissions}
            loading={loading || fetching || isLoadingAfterUpdate}
            onAddPermission={addPermission}
            onRemovePermission={removePermission}
          />
        </Tabs.TabPane>
        <Tabs.TabPane
          style={{ marginTop: 8 }}
          key="single-security-group"
          tab={intl.formatMessage({ id: 'single-security-group' })}>
          <SingleGroupTab
            folder={folder}
            permissionList={currentPermissions}
            loading={loading || fetching || isLoadingAfterUpdate}
            onAddPermission={addPermission}
            onRemovePermission={removePermission}
          />
        </Tabs.TabPane>
      </Tabs>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({});

interface UpdatePermissionsParams {
  projectId: string;
  addedPermissions: Array<
    AlteredDbFolderPermission | CreateFolderPermissionParams['permissionData']
  >;
  commit: DbCommit;
  removedPermissions: DbFolderPermission[];
}
const updatePermissions = async ({
  projectId,
  addedPermissions,
  removedPermissions,
  commit,
}: UpdatePermissionsParams) => {
  const addPromises = addedPermissions.map((permissionData) => {
    const alteredPermission = permissionData as AlteredDbFolderPermission;
    if (alteredPermission.altered) {
      const { altered, ...permission } = alteredPermission;
      return updateFolderPermission({ projectId, permission, commit });
    }

    return createFolderPermission({ projectId, permissionData, commit });
  });
  const removePromises = removedPermissions.map((permission) =>
    deleteFolderPermission({ projectId, permissionId: permission.id, commit })
  );

  return Promise.all([...addPromises, ...removePromises]);
};
